import React, { useState } from "react";
import { useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowLeft, Star, Clock, Briefcase, Award, MessageSquare,
  Heart, Share2, Calendar, CheckCircle, Building2, Loader2
} from "lucide-react";
import Layout from "../components/layout/Layout";
import ReviewCard from "../components/ui/ReviewCard";
import ExpertTierBadge from "../components/ui/ExpertTierBadge";
import { useFavorites } from "../context/FavoritesContext";
import { useAuth } from "../context/AuthContext";
import { useMessaging } from "../context/MessagingContext";
import { useReviews } from "../context/ReviewsContext";
import { useExpert } from "../hooks/useExperts";

export default function ExpertProfilePage() {
  const { id } = useParams();
  const location = useLocation();
  const previewProfile = location.state?.previewProfile;

  const isPreview = id === "preview" && previewProfile;

  const { expert: dbExpert, loading: expertLoading } = useExpert(isPreview ? null : id);

  const expert = isPreview
    ? {
        id: "preview",
        name: previewProfile.name || "Эксперт",
        avatar: previewProfile.avatarUrl || "",
        title: previewProfile.title || "",
        specializations: previewProfile.specializations || [],
        experience: 0,
        rating: 0,
        hourlyRate: previewProfile.hourlyRate || 0,
        bio: previewProfile.bio || "",
        companies: previewProfile.companies || [],
        achievements: previewProfile.achievements || [],
        reviews: [],
        available: previewProfile.available ?? true,
        expertTier: "regular"
      }
    : dbExpert;

  const { isFavorite, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const { getOrCreateConversation } = useMessaging();
  const [activeTab, setActiveTab] = useState("about");
  const navigate = useNavigate();
  const { getReviewsForExpert } = useReviews();
  const [dbReviews, setDbReviews] = useState([]);
  const [dbReviewsLoaded, setDbReviewsLoaded] = useState(false);

  useEffect(() => {
    if (expert && !dbReviewsLoaded) {
      const load = async () => {
        const reviews = await getReviewsForExpert(expert.id);
        setDbReviews(reviews);
        setDbReviewsLoaded(true);
      };
      load();
    }
  }, [expert, dbReviewsLoaded, getReviewsForExpert]);

  const displayReviews = dbReviewsLoaded ? dbReviews : expert?.reviews || [];

  if (expertLoading && !isPreview) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!expert && !expertLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-medium text-white mb-4">
              Эксперт не найден
            </h1>
            <Link
              to="/experts"
              className="text-primary-400 hover:text-primary-300 text-lg font-extralight"
            >
              Вернуться к каталогу
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const favorite = isFavorite(expert.id);

  const tabs = [
    { id: "about", label: "О себе" },
    { id: "experience", label: "Опыт" },
    { id: "reviews", label: `Отзывы (${displayReviews.length})` }
  ];

  const reviewsToShow = displayReviews.slice(0, 3);

  const handleWriteMessage = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    const conversation = await getOrCreateConversation(
      expert.id,
      expert.name,
      expert.avatar
    );
    if (conversation) {
      navigate(`/messages?conv=${conversation.id}`);
    }
  };

  return (
    <Layout>
      <div id="expert-profile_page" className="min-h-screen">
        <section className="relative py-8 lg:py-12 bg-gradient-to-b from-primary-950/30 to-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {isPreview ? (
              <div className="mb-6 flex items-center gap-4">
                <Link
                  to="/expert/dashboard"
                  className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-lg font-extralight">Назад к редактированию</span>
                </Link>
                <span className="px-3 py-1 rounded-full bg-yellow-500/10 text-yellow-400 text-lg font-extralight">
                  Режим превью
                </span>
              </div>
            ) : (
              <Link
                to="/experts"
                className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-lg font-extralight">Назад к каталогу</span>
              </Link>
            )}

            <div className="flex flex-col lg:flex-row gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="lg:w-80 flex-shrink-0"
              >
                <div className={`relative rounded-2xl overflow-hidden ${
                  expert.expertTier && expert.expertTier !== "regular"
                    ? "ring-2 ring-amber-500/40"
                    : ""
                }`}>
                  {expert.avatar ? (
                    <img
                      src={expert.avatar}
                      alt={expert.name}
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                      <span className="text-white text-6xl font-medium">
                        {expert.name?.[0] || "?"}
                      </span>
                    </div>
                  )}
                  {!expert.available && (
                    <div className="absolute bottom-4 left-4 right-4 px-4 py-2 rounded-md bg-neutral-900/90 backdrop-blur-sm text-center text-lg font-extralight text-neutral-300">
                      Временно недоступен
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => toggleFavorite(expert.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-md border transition-colors ${
                      favorite
                        ? "bg-red-500/10 border-red-500/30 text-red-400"
                        : "border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500"
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${favorite ? "fill-current" : ""}`} />
                    <span className="text-lg font-extralight">
                      {favorite ? "В избранном" : "В избранное"}
                    </span>
                  </button>
                  <button className="p-3 rounded-md border border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500 transition-colors">
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex-1"
              >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  {expert.expertTier && expert.expertTier !== "regular" && (
                    <ExpertTierBadge tier={expert.expertTier} size="lg" />
                  )}
                  {expert.specializations.map((spec) => (
                    <span
                      key={spec}
                      className="px-3 py-1 rounded-full bg-primary-500/10 text-primary-400 text-lg font-extralight"
                    >
                      {spec}
                    </span>
                  ))}
                </div>

                <h1 className="text-3xl lg:text-4xl font-medium text-white mb-2">
                  {expert.name}
                </h1>
                <p className="text-xl font-extralight text-neutral-400 mb-6">
                  {expert.title}
                </p>

                <div className="flex flex-wrap items-center gap-6 mb-6">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-xl font-medium text-white">
                      {displayReviews.length > 0
                        ? (displayReviews.reduce((sum, r) => sum + r.rating, 0) / displayReviews.length).toFixed(1)
                        : expert.rating}
                    </span>
                    <span className="text-lg font-extralight text-neutral-400">
                      ({displayReviews.length} отзывов)
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-neutral-400">
                    <Clock className="w-5 h-5" />
                    <span className="text-lg font-extralight">{expert.experience} лет опыта</span>
                  </div>
                </div>

                <div className="p-6 rounded-md bg-neutral-800/50 border border-neutral-700 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-3xl font-medium text-white">
                        {expert.hourlyRate.toLocaleString("ru-RU")} ₽
                      </span>
                      <span className="text-lg font-extralight text-neutral-400"> / час</span>
                    </div>
                    <div
                      className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                        expert.available
                          ? "bg-green-500/10 text-green-400"
                          : "bg-neutral-700 text-neutral-400"
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${
                          expert.available ? "bg-green-400" : "bg-neutral-500"
                        }`}
                      />
                      <span className="text-lg font-extralight">
                        {expert.available ? "Доступен" : "Недоступен"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      disabled={!expert.available}
                      onClick={() => expert.available && navigate(`/booking/${expert.id}`)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
                    >
                      <Calendar className="w-5 h-5" />
                      Записаться на консультацию
                    </button>
                    <button
                      onClick={handleWriteMessage}
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-neutral-700 hover:border-primary-600 hover:bg-primary-600/10 text-white text-lg font-extralight transition-colors"
                    >
                      <MessageSquare className="w-5 h-5" />
                      Написать
                    </button>
                  </div>
                </div>

                <div className="border-b border-neutral-800">
                  <div className="flex gap-6 overflow-x-auto">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`pb-4 text-lg font-extralight whitespace-nowrap transition-colors relative ${
                          activeTab === tab.id
                            ? "text-white"
                            : "text-neutral-400 hover:text-neutral-200"
                        }`}
                      >
                        {tab.label}
                        {activeTab === tab.id && (
                          <motion.div
                            layoutId="activeTab"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-8 lg:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:pl-[21rem]">
              {activeTab === "about" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8"
                >
                  <div>
                    <h2 className="text-2xl font-medium text-white mb-4">О себе</h2>
                    <p className="text-lg font-extralight text-neutral-300 leading-relaxed">
                      {expert.bio}
                    </p>
                  </div>

                  <div>
                    <h2 className="text-2xl font-medium text-white mb-4">Достижения</h2>
                    <ul className="space-y-3">
                      {expert.achievements.map((achievement, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                          <span className="text-lg font-extralight text-neutral-300">
                            {achievement}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}

              {activeTab === "experience" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-medium text-white mb-4">Опыт работы</h2>
                  <div className="space-y-4">
                    {expert.companies.map((company, index) => (
                      <div
                        key={index}
                        className="p-5 rounded-md bg-neutral-800/50 border border-neutral-700"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-md bg-neutral-700 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-6 h-6 text-neutral-400" />
                          </div>
                          <div>
                            <h3 className="text-xl font-medium text-white mb-1">
                              {company.name}
                            </h3>
                            <p className="text-lg font-extralight text-neutral-400 mb-2">
                              {company.position}
                            </p>
                            <div className="flex items-center gap-2 text-lg font-extralight text-neutral-500">
                              <Calendar className="w-4 h-4" />
                              {company.years}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {activeTab === "reviews" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-medium text-white">Отзывы</h2>
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                      <span className="text-xl font-medium text-white">
                        {displayReviews.length > 0
                          ? (displayReviews.reduce((sum, r) => sum + r.rating, 0) / displayReviews.length).toFixed(1)
                          : expert.rating}
                      </span>
                      <span className="text-lg font-extralight text-neutral-400">
                        ({displayReviews.length} отзывов)
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {reviewsToShow.map((review, index) => (
                      <ReviewCard key={index} review={review} />
                    ))}
                  </div>

                  {displayReviews.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                      <p className="text-lg font-extralight text-neutral-400">
                        Пока нет отзывов
                      </p>
                    </div>
                  ) : displayReviews.length > 3 ? (
                    <Link
                      to={`/experts/${expert.id}/reviews`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-300 hover:text-white text-lg font-extralight transition-colors"
                    >
                      Все отзывы ({displayReviews.length})
                    </Link>
                  ) : null}
                </motion.div>
              )}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
